import { Box, Link, MenuItem, Skeleton } from '@mui/material';
import { FC, MouseEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectJiraIssues,
    selectJiraIssuesCount,
    selectGetJiraIssuesStatus,
    selectGetJiraIssuesError,
} from '../store/selectors';
import { getJiraIssuesRequest, getJiraIssuesReset, resetArrayJiraIssues } from '../store';
import { RequestStatusEnum } from '@shared/types';
import { usePrevious } from '@shared/hooks/usePrevious';
import { useListState } from '@shared/hooks/useListState';
import useHandleScroll from '@shared/hooks/useHandleScroll';
import UtmsSearchTextField from '@shared/components/ui/UtmsSearchTextField';
import HelperText from '@shared/components/others/HelperText/HelperText';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { JiraIssue } from '../types';
import { useDefectsChunk } from '../hooks/useDefectsChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';

interface JiraIssueSelectorMenuProps {
    open: boolean;
    onClose: () => void;
    onChange: (issue: JiraIssue) => void;
}

const SKELETONS_COUNT = 6;

const JiraIssueSelectorMenu = (props: JiraIssueSelectorMenuProps) => (
    <ComponentWithChunks
        featuresArray={['defects']}
        hooksArray={[useDefectsChunk]}
        Component={JiraIssueSelectorMenuComponent}
        {...props}
    />
);

const JiraIssueSelectorMenuComponent: FC<JiraIssueSelectorMenuProps> = ({ open, onChange, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const getJiraIssuesStatus = useSelector(selectGetJiraIssuesStatus);
    const isLoading = getJiraIssuesStatus === RequestStatusEnum.Loading;
    const isResettingRef = useRef(false);
    const jiraIssues = useSelector(selectJiraIssues);
    const jiraIssuesCount = useSelector(selectJiraIssuesCount);

    const { setPagination, globalFilter, setGlobalFilter } = useListState(
        'jiraIssueSelector',
        false,
        getJiraIssuesRequest,
        { disableUrlParams: true },
    );

    const prevGlobalFilter = usePrevious(globalFilter);
    const allFetched = jiraIssuesCount === 0;

    const handleScroll = useHandleScroll({
        isLoading,
        setPagination,
        allItemsFetched: allFetched,
    });

    const handleSelectIssue = (issue: JiraIssue) => (_e: MouseEvent<HTMLElement>) => {
        onChange(issue);
    };

    useEffect(() => {
        if (prevGlobalFilter !== undefined && globalFilter !== prevGlobalFilter) {
            isResettingRef.current = true;
            dispatch(resetArrayJiraIssues());
        }
    }, [dispatch, globalFilter, prevGlobalFilter]);

    useEffect(() => {
        if (!open) {
            isResettingRef.current = true;
            dispatch(resetArrayJiraIssues());
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (!isLoading) {
            isResettingRef.current = false;
        }
    }, [isLoading]);

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                <UtmsSearchTextField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                <Box sx={{ maxHeight: 300, overflow: 'auto' }} onScroll={handleScroll}>
                    {jiraIssues.map((issue: JiraIssue) => (
                        <MenuItem key={issue.key} onClick={handleSelectIssue(issue)}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Link
                                    href={issue.jiraLink}
                                    underline="hover"
                                    target="_blank"
                                    sx={{ cursor: 'pointer', flexShrink: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {issue.key}
                                </Link>
                                <OverflowTooltip title={issue.name}>{issue.name}</OverflowTooltip>
                            </Box>
                        </MenuItem>
                    ))}
                    {isLoading &&
                        [...Array(SKELETONS_COUNT)].map((_, i) => (
                            <MenuItem key={i}>
                                <Skeleton animation="wave" variant="text" width="100%" />
                            </MenuItem>
                        ))}
                    {getJiraIssuesStatus === RequestStatusEnum.Success && !jiraIssues.length && (
                        <HelperText sx={{ fontSize: '0.86rem' }}>{t('no_record_to_display')}...</HelperText>
                    )}
                </Box>
            </Box>

            {getJiraIssuesStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('jira_issues_r')}
                    action={t('fetching')}
                    resetActionCallback={getJiraIssuesReset}
                    errorSelector={selectGetJiraIssuesError}
                />
            )}
        </>
    );
};

export default JiraIssueSelectorMenu;
